// Зеркало протокола из ru.clapme.live.Protocol — держим руками в синхроне.
export type SlideKind = 'EMOJI_STORM' | 'POLL_SINGLE' | 'POLL_MULTI' | 'FREE_TEXT'
export type SlidePhase = 'IDLE' | 'OPEN' | 'CLOSED' | 'REVEALED'
export type ResultView = 'BARS' | 'CLOUD' | 'EMOJI_FLOW'

export interface Option {
  id: string
  text: string
  /** null — участнику правильность пока не раскрыта. */
  correct: boolean | null
}

export interface Slide {
  id: string
  kind: SlideKind
  title: string
  phase: SlidePhase
  options: Option[]
  timerSeconds: number | null
  maxTextLength: number
  resultView: ResultView
  emojiSet: string[]
  moderated: boolean
  scored: boolean
}

export interface StateMsg {
  t: 'state'
  index: number
  total: number
  slide: Slide | null
  participants: number
  /** Сколько уже ответило на текущем слайде. */
  answered: number
  msLeft: number | null
  ended: boolean
  /** Вся презентация приходит только пульту: участнику и проектору — null. */
  deck: Slide[] | null
}

export interface Bar {
  optionId: string
  text: string
  count: number
  correct: boolean | null
}

export interface CloudItem {
  text: string
  count: number
  masked: boolean
  pending: boolean
}

export interface EmojiCount {
  emoji: string
  count: number
}

export interface PendingText {
  participantId: string
  name: string
  text: string
  masked: boolean
}

export interface ResultsMsg {
  t: 'results'
  slideId: string
  kind: SlideKind
  view: ResultView
  total: number
  revealed: boolean
  bars: Bar[]
  cloud: CloudItem[]
  emoji: EmojiCount[]
  /** Очередь премодерации — приходит только пульту. */
  pending: PendingText[]
}

export interface MeMsg {
  t: 'me'
  score: number
  answered: boolean
  picked: string[]
  text: string | null
  awarded: number | null
  correctIds: string[] | null
}

export interface LeaderRow {
  participantId: string
  name: string
  score: number
}

export interface WelcomeMsg {
  t: 'welcome'
  participantId: string
  name: string
  code: string
  title: string
}

export interface TapsMsg {
  t: 'taps'
  slideId: string
  deltas: Record<string, number>
}

export interface LeaderboardMsg {
  t: 'leaderboard'
  rows: LeaderRow[]
}

export interface ParticipantRow {
  participantId: string
  name: string
  score: number
  /** На связи ли человек прямо сейчас. */
  connected: boolean
  /** Ответил ли на текущем слайде. */
  answered: boolean
}

export interface ParticipantsMsg {
  t: 'participants'
  count: number
  connected: number
  answered: number
  /** Сколько строк реально пришло: счётчики выше — по всему залу. */
  shown: number
  rows: ParticipantRow[]
}

export interface ErrorMsg {
  t: 'error'
  code: string
  message: string
}

export type Incoming =
  | StateMsg
  | ResultsMsg
  | MeMsg
  | WelcomeMsg
  | TapsMsg
  | LeaderboardMsg
  | ParticipantsMsg
  | ErrorMsg

export interface SlideSpec {
  kind: SlideKind
  title: string
  options?: { text: string; correct?: boolean }[]
  timerSeconds?: number | null
  maxTextLength?: number
  resultView?: ResultView | null
  emojiSet?: string[] | null
  moderated?: boolean
}

/**
 * Слайд демонстрации — SlideSpec плюс стабильный id: на нём держатся ключи списка
 * в редакторе, и без него каждая правка меняла бы всю вёрстку.
 */
export interface DeckSlideSpec {
  id: string
  kind: SlideKind
  title: string
  options: { text: string; correct?: boolean }[]
  timerSeconds: number | null
  maxTextLength: number
  resultView: ResultView | null
  emojiSet: string[] | null
  moderated: boolean
}

export interface DeckSummary {
  id: string
  title: string
  slideCount: number
  /** Номер версии для оптимистичного сохранения: PUT несёт тот, что читал. */
  revision: number
  updatedAt: string
}

export interface Deck extends DeckSummary {
  slides: DeckSlideSpec[]
}

/** Запуск демонстрации в истории: числа могут быть null, пока регламентный писака не допишет конец. */
export interface RunSummary {
  code: string
  startedAt: string
  endedAt: string | null
  participants: number | null
  /** Эфир сейчас в памяти и не завершён. */
  live: boolean
}

export interface PlanLimits {
  maxDecks: number
  maxSlides: number
  maxParticipants: number
}

export interface PlanInfo {
  code: string
  title: string
  priceKopecks: number
  periodDays: number
  limits: PlanLimits
}

export interface BillingMe {
  plan: PlanInfo
  validUntil: string | null
  payments: { id: string; planCode: string; amountKopecks: number; status: string }[]
}

/** Общее между живым слайдом и слайдом демонстрации — то, что умеет править форма. */
export type EditableSlide = {
  kind: SlideKind
  title: string
  options: { text: string; correct?: boolean | null }[]
  timerSeconds: number | null
  maxTextLength: number
  resultView: ResultView | null
  emojiSet: string[] | null
  moderated: boolean
}

export interface SessionInfo {
  code: string
  title: string
  participants: number
  slides: number
  currentIndex: number
  ended: boolean
}

export const KIND_LABEL: Record<SlideKind, string> = {
  EMOJI_STORM: 'Эмодзи-шторм',
  POLL_SINGLE: 'Один ответ',
  POLL_MULTI: 'Несколько ответов',
  FREE_TEXT: 'Свободный текст',
}
